import { Component, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { TransactionService } from 'src/app/services/transaction.service';
import { CartService } from 'src/app/services/cart.service';
import { Product } from 'src/app/entities/product';
import { CartItem } from 'src/app/entities/cart-item';
import { AlertService } from 'src/app/services/alert.service';
import { ProductService } from 'src/app/services/product.service';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-step-two',
  templateUrl: './step-two.component.html',
  styleUrls: ['./step-two.component.scss'],
  animations: [
    trigger(
      'showQuestion', [
        transition(
          ':enter', [
            style( { opacity: 0, transform: 'translateY(50%)' } ),
            animate( '300ms ease-in-out', style( { opacity: 1, transform: 'translateY(0%)' } ) )
          ]
        )
      ]
    )
  ]
})
export class StepTwoComponent implements OnInit, OnDestroy {

  @Output() clickContinue = new EventEmitter<any>();
  @Output() clickBack = new EventEmitter<any>();

  isCreditCard = false;
  isMutualCard = false;
  isCash = false;
  isMercadoPagoQR = false;
  cardName = '';
  cardNumber = '';
  cardMonth = '';
  cardYear = '';
  cardCode = '';
  cardInstallment: any;
  cardNumberDisplay = '';
  cartLoaded = false;
  cart = new Array<CartItem>();
  itemNoStock = new CartItem();
  formData: any;
  discounts = [];
  installments = [];
  stockAvailable = true;
  appliedCouponCode: string | null = null;
  mercadoPagoQRData: any = null;
  loadingQR = false;
  qrError = false;
  pollingInterval: any = null;
  pollingAttempts = 0;
  maxPollingAttempts = 60; // 5 minutes (60 attempts * 5 seconds)
  isPolling = false;

  constructor(
    protected cartService: CartService,
    protected transactionService: TransactionService,
    protected alertService: AlertService,
    protected productService: ProductService
  ) { }

  ngOnInit() {
    this.loadInstallments();
    this.loadDiscounts();
    this.loadCart();
    
    // Load coupon from transaction if it exists
    if (this.transactionService.transactionCurrent.coupon_code) {
      this.appliedCouponCode = this.transactionService.transactionCurrent.coupon_code;
    }
    
    // Subscribe to applied coupon changes
    this.transactionService.appliedCoupon.subscribe(coupon => {
      if (coupon) {
        this.appliedCouponCode = coupon.code;
        
        // Regenerate QR if MercadoPago QR is selected
        if (this.isMercadoPagoQR && this.mercadoPagoQRData) {
          this.generateMercadoPagoQR();
        }
      } else {
        // Clear the coupon when it's removed
        this.appliedCouponCode = null;
        
        // Regenerate QR if MercadoPago QR is selected
        if (this.isMercadoPagoQR && this.mercadoPagoQRData) {
          this.generateMercadoPagoQR();
        }
      }
    });
    
    this.transactionService.createEndpoint(
      this.transactionService.transactionCurrent.id,
      this.cartService.fingerprintCurrent,
      this.transactionService.email).toPromise().then(data => {
      if (!data.success) {
        alert(data.error.message);
        return;
      }
      this.formData = data.response;
    });

    // Initialize Feather icons
    setTimeout(() => {
      if (typeof (window as any).feather !== 'undefined') {
        (window as any).feather.replace();
      }
    }, 0);
  }

  ngOnDestroy() {
    // Clean up polling interval when component is destroyed
    this.stopPolling();
  }

  onChangePaymentCreditCard() {
    if (this.isCreditCard) {
      this.isMutualCard = false;
      this.isCash = false;
      this.isMercadoPagoQR = false;
      this.cardInstallment = undefined;
      this.applyDiscount(0);
      // Don't stop polling - user might still pay with MercadoPago QR in another tab
    }
    // Refresh feather icons
    setTimeout(() => {
      if (typeof (window as any).feather !== 'undefined') {
        (window as any).feather.replace();
      }
    }, 0);
  }

  onChangePaymentMutual() {
    if (this.isMutualCard) {
      this.isCreditCard = false;
      this.isCash = false;
      this.isMercadoPagoQR = false;
      this.cardInstallment = undefined;
      this.applyDiscount(1);
      // Don't stop polling - user might still pay with MercadoPago QR in another tab
    }
    // Refresh feather icons
    setTimeout(() => {
      if (typeof (window as any).feather !== 'undefined') {
        (window as any).feather.replace();
      }
    }, 0);
  }

  onChangePaymentCash() {
    if (this.isCash) {
      this.isMutualCard = false;
      this.isCreditCard = false;
      this.isMercadoPagoQR = false;
      this.isCash = true;
      this.applyDiscount(2);
      // Don't stop polling - user might still pay with MercadoPago QR in another tab
    }
    // Refresh feather icons
    setTimeout(() => {
      if (typeof (window as any).feather !== 'undefined') {
        (window as any).feather.replace();
      }
    }, 0);
  }

  onChangePaymentMercadoPagoQR() {
    if (this.isMercadoPagoQR) {
      this.isMutualCard = false;
      this.isCreditCard = false;
      this.isCash = false;
      this.applyDiscount(2); // Same discount as cash
      this.generateMercadoPagoQR();
    }
    // Refresh feather icons
    setTimeout(() => {
      if (typeof (window as any).feather !== 'undefined') {
        (window as any).feather.replace();
      }
    }, 0);
  }

  generateMercadoPagoQR() {
    this.loadingQR = true;
    this.qrError = false;
    this.mercadoPagoQRData = null;
    this.stopPolling(); // Stop any existing polling

    this.transactionService.generateMercadoPagoQR(this.cartService.fingerprintCurrent, this.appliedCouponCode).subscribe(
      data => {
        this.loadingQR = false;
        if (data.success) {
          this.mercadoPagoQRData = data.response;
          
          // Prepare payment data (but don't send yet - only when payment is confirmed)
          this.transactionService.transactionCurrent.payment_type = 3;
          if (this.transactionService.transactionCurrent.data_payment == undefined) {
            this.transactionService.transactionCurrent.data_payment = {};
          }
          this.transactionService.transactionCurrent.data_payment.mercadopago = this.mercadoPagoQRData;
          
          // Start polling for payment status
          this.startPolling();
          
          // Refresh feather icons after QR is displayed
          setTimeout(() => {
            if (typeof (window as any).feather !== 'undefined') {
              (window as any).feather.replace();
            }
          }, 100);
        } else {
          this.qrError = true;
          this.alertService.showError('Error', 'No se pudo generar el código QR de MercadoPago. Intente nuevamente.');
        }
      },
      error => {
        this.loadingQR = false;
        this.qrError = true;
        this.alertService.showError('Error', 'No se pudo generar el código QR de MercadoPago. Intente nuevamente.');
        console.error('Error generating MercadoPago QR:', error);
      }
    );
  }

  startPolling() {
    if (this.isPolling) {
      return; // Already polling
    }

    this.isPolling = true;
    this.pollingAttempts = 0;

    this.pollingInterval = setInterval(() => {
      this.pollingAttempts++;
      
      // Poll transaction status
      this.transactionService.fetchTransaction(
        this.transactionService.transactionCurrent.id,
        this.cartService.fingerprintCurrent,
        this.transactionService.email
      ).subscribe(
        data => {
          if (data.success && data.response) {
            const transaction = data.response;
            
            // Status 1 = Approved/Confirmed
            if (transaction.status === 1) {
              this.stopPolling();
              
              // Payment was confirmed via MercadoPago - override selected payment method
              // This handles the case where user switched to another payment method
              // but still completed payment via MercadoPago QR
              this.isMercadoPagoQR = true;
              this.isCreditCard = false;
              this.isMutualCard = false;
              this.isCash = false;
              
              // Save with MercadoPago payment type
              this.submitForm();
            } 
            // Status 2 = Rejected/Failed
            else if (transaction.status === 2) {
              this.stopPolling();
              this.alertService.showError('Pago Rechazado', 'Tu pago fue rechazado. Por favor intenta nuevamente.');
            }
            // Status 0 = Pending - keep polling
            else if (this.pollingAttempts >= this.maxPollingAttempts) {
              // Timeout after max attempts
              this.stopPolling();
              this.alertService.showError('Tiempo de espera agotado', 'No hemos recibido confirmación del pago. Te notificaremos por email cuando se procese.');
            }
          }
        },
        error => {
          console.error('Error polling transaction status:', error);
          // Don't stop polling on error, just skip this attempt
        }
      );
    }, 5000); // Poll every 5 seconds
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
      this.isPolling = false;
      this.pollingAttempts = 0;
    }
  }

  clearMercadoPagoData() {
    this.mercadoPagoQRData = null;
    this.loadingQR = false;
    this.qrError = false;
    if (this.transactionService.transactionCurrent.data_payment) {
      delete this.transactionService.transactionCurrent.data_payment.mercadopago;
    }
  }

  retryGenerateQR() {
    this.generateMercadoPagoQR();
    // Refresh feather icons
    setTimeout(() => {
      if (typeof (window as any).feather !== 'undefined') {
        (window as any).feather.replace();
      }
    }, 100);
  }

  getTotal() {
    return this.transactionService.transactionCurrent.total;
  }

  getInstallmentsByCreditCard() {
    return this.installments
      .filter(i => i.type == 0)
      .sort((a, b) => {
        const numA = parseInt(a.installment) || 0;
        const numB = parseInt(b.installment) || 0;
        return numA - numB;
      });
  }

  getInstallmentsByMutualCard() {
    return this.installments
      .filter(i => i.type == 1)
      .sort((a, b) => {
        const numA = parseInt(a.installment) || 0;
        const numB = parseInt(b.installment) || 0;
        return numA - numB;
      });
  }

  onClickSubmit() {
    // Validate that a payment method has been selected
    if (!this.isCreditCard && !this.isMutualCard && !this.isCash && !this.isMercadoPagoQR) {
      this.alertService.showError('Error!', 'Debe seleccionar un método de pago para continuar.');
      return;
    }

    // Validate installments for credit and mutual cards
    if ((this.isCreditCard || this.isMutualCard) && !this.cardInstallment) {
      this.alertService.showError('Error!', 'Debe seleccionar el número de cuotas.');
      return;
    }

    this.stockAvailable = true;
    this.checkStock();
  }

  isPaymentMethodSelected(): boolean {
    return this.isCreditCard || this.isMutualCard || this.isCash || this.isMercadoPagoQR;
  }

  checkStock(){
    var response = [];
    //chequeo el stock para cada producto antes de proceder
    this.cart.forEach(element => {
      var product;
      var stock;
      this.productService.fetchById(element.product_id).toPromise().then((data => {
        response.push(data);
        if (data.success) {
          product = data.response;
          stock = Product.getStock(product);
        }
        if (stock < element.quantity) {
          this.stockAvailable = false;
          this.itemNoStock = element;
        }
        if(response.length === this.cart.length){
          this.submitForm();
        }
      }));
    });
  }

  submitForm(){
    if (!this.stockAvailable) {
      this.alertService.showError('Error!', 'No hay stock disponible del producto ' + this.itemNoStock.product.title + ' , lo sentimos.');
      return;
    }

    if (this.isMutualCard) {
      if (this.cardName == '' || this.cardNumber == '' || this.cardMonth == '' || this.cardYear == '' || this.cardCode == '') {
        this.alertService.showError('Error!', 'Todos los campos son obligatorios.');
        return;
      }

      this.transactionService.transactionCurrent.payment_type = 1;
      //MutualCard
      if (this.transactionService.transactionCurrent.data_payment == undefined) {
        this.transactionService.transactionCurrent.data_payment = {};
      }
      this.transactionService.transactionCurrent.data_payment.card_name = this.cardName;
      this.transactionService.transactionCurrent.data_payment.card_number = this.cardNumber;
      this.transactionService.transactionCurrent.data_payment.card_month = this.cardMonth;
      this.transactionService.transactionCurrent.data_payment.card_year = this.cardYear;
      this.transactionService.transactionCurrent.data_payment.card_cvv = this.cardCode;
      this.transactionService.transactionCurrent.data_payment.installment = this.cardInstallment;
      
     
      this.transactionService.sendCard(
        this.cartService.fingerprintCurrent,
        this.transactionService.transactionCurrent.id,
        this.transactionService.transactionCurrent.data_payment,
        1,
        this.appliedCouponCode
      ).subscribe(data => {
        if (data.success) {
          this.clickContinue.emit();
        }
      });
      //FirstData
    } else if (this.isCreditCard) {
      this.transactionService.transactionCurrent.payment_type = 0;
      this.transactionService.transactionCurrent.data_payment.installment = this.cardInstallment;
      // this.transactionService.transactionCurrent.data_payment.installment.percent = '13,50';
      this.transactionService.sendCard(
        this.cartService.fingerprintCurrent,
        this.transactionService.transactionCurrent.id,
        this.transactionService.transactionCurrent.data_payment,
        0,
        this.appliedCouponCode
      ).subscribe(data => {
        if (data.success) {
          let form: any = document.getElementById('checkoutform');
          form.action = data.response.endpoint;

          let values = [
            { name: 'checkoutoption', value: 'combinedpage' },
            { name: 'txntype', value: 'sale' },
            { name: 'timezone', value: 'America/Buenos_Aires' },
            { name: 'txndatetime', value: data.response.datetime },
            { name: 'hash_algorithm', value: 'SHA256' },
            { name: 'hash', value: data.response.hash },
            { name: 'storename', value: data.response.store_id },
            { name: 'currency', value: data.response.currency },
            { name: 'chargetotal', value: data.response.total },
            { name: 'responseFailURL', value: data.response.return_url },
            { name: 'responseSuccessURL', value: data.response.return_url },
            { name: 'numberOfInstallments', value: this.cardInstallment.installment }
          ];

          for (const item of values) {
            var element1 = document.createElement("input");
            element1.name = item.name;
            element1.value = item.value;
            element1.type = 'hidden'
            form.appendChild(element1);
          }

          form.submit();
        }
      });
      //Efectivo
    } else if (this.isCash) {
      this.transactionService.transactionCurrent.payment_type = 2;
      if (this.transactionService.transactionCurrent.data_payment == undefined) {
        this.transactionService.transactionCurrent.data_payment = {};
      }

      // Don't send MercadoPago data if user switched to cash/transfer
      const paymentData = { ...this.transactionService.transactionCurrent.data_payment };
      delete paymentData.mercadopago;

      this.transactionService.sendCard(
        this.cartService.fingerprintCurrent,
        this.transactionService.transactionCurrent.id,
        paymentData,
        2,
        this.appliedCouponCode
      ).subscribe(data => {
        if (data.success) {
          this.clickContinue.emit();
        }
      });
    } else if (this.isMercadoPagoQR) {
      // MercadoPago QR - payment will be processed via webhook
      // Just continue to next step to show confirmation
      if (!this.mercadoPagoQRData) {
        this.alertService.showError('Error', 'Por favor espere a que se genere el código QR.');
        return;
      }

      this.transactionService.transactionCurrent.payment_type = 3; // New payment type for MercadoPago QR
      if (this.transactionService.transactionCurrent.data_payment == undefined) {
        this.transactionService.transactionCurrent.data_payment = {};
      }

      // Store MercadoPago data
      this.transactionService.transactionCurrent.data_payment.mercadopago = this.mercadoPagoQRData;

      this.transactionService.sendCard(
        this.cartService.fingerprintCurrent,
        this.transactionService.transactionCurrent.id,
        this.transactionService.transactionCurrent.data_payment,
        3,
        this.appliedCouponCode
      ).subscribe(data => {
        if (data.success) {
          this.clickContinue.emit();
        }
      });
    }
  }

  onClickBack() {
    this.transactionService.transactionCurrent.data_payment.delivery = null;
    this.transactionService.paymentDiscount.next(0);
    this.clickBack.emit();
  }

  applyInstallments() {
    this.transactionService.paymentInstallment.next(this.cardInstallment);
  }

  applyDiscount(type) {
    this.transactionService.paymentDiscount.next(this.getDiscountByType(type));
  }

  getDiscountByType(type) {
    for (const item of this.discounts) {
      if (item.type_id == type) {
        return item.percent;
      }
    }
    return 0;
  }

  loadDiscounts() {
    this.transactionService.listPaymentDiscounts().subscribe(data => {
      if (data.success) {
        this.discounts = data.response;
      }
    });
  }

  loadInstallments() {
    this.transactionService.listPaymentInstallments().subscribe(data => {
      if (data.success) {
        this.installments = data.response;
      }
    });
  }

  loadCart() {
    this.cartService.items.subscribe(data => {
      if (data.length == 0 && !this.cartLoaded) {
        // Buscar listado
        this.cartService.fetchList().subscribe(data => {
          this.getTotal();
          this.cartLoaded = true;
        });
        return;
      }

      this.cart = data;
      this.getTotal();
    });
  }

  // Format card number with spaces every 4 digits
  onCardNumberInput(event: any) {
    let value = event.target.value.replace(/\s/g, ''); // Remove all spaces
    value = value.replace(/\D/g, ''); // Remove all non-digits
    
    if (value.length > 16) {
      value = value.substring(0, 16);
    }
    
    this.cardNumber = value;
    
    // Format with spaces every 4 digits
    let formatted = value.match(/.{1,4}/g)?.join(' ') || '';
    this.cardNumberDisplay = formatted;
    event.target.value = formatted;
  }

  // Only allow letters and spaces for name
  onCardNameInput(event: any) {
    let value = event.target.value;
    value = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '');
    this.cardName = value;
    event.target.value = value;
  }

  // Only allow 3 digits for CVV
  onCvvInput(event: any) {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 3) {
      value = value.substring(0, 3);
    }
    this.cardCode = value;
    event.target.value = value;
  }

}
